import React, { useState, useEffect } from 'react';
import {
  FileText,
  Download,
  FilePen,
  CloudUpload,
  FolderOpen,
  Link,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Check,
  Eye,
  Sidebar,
} from 'lucide-react';

// Import dummy data from data.js
import { recentUploads, companySizeOptions, countryOptions, fileFormatOptions, customerOptions } from './data';

// A mapping for icon names to actual Lucide React components
const iconComponents = {
  FileText: FileText,
  FilePen: FilePen,
  // Add other icons if you plan to use them dynamically from data
};

// B2BRegister component
export default function B2BRegister() {
  // State for customer type radio buttons
  const [customerType, setCustomerType] = useState('new');
  // State for company name input
  const [companyName, setCompanyName] = useState('');
  // State for company size select
  const [companySize, setCompanySize] = useState('');
  // State for country/region select
  const [country, setCountry] = useState('');
  // State to control visibility of File Upload & Configuration section
  const [showFileUploadSection, setShowFileUploadSection] = useState(false);

  // State for customer name select in file upload
  const [fileUploadCustomer, setFileUploadCustomer] = useState('');
  // State for file format select
  const [fileFormat, setFileFormat] = useState('');
  // State for document type radio buttons
  const [docType, setDocType] = useState('EDI 850');
  // State for validation settings checkboxes
  const [validationSettings, setValidationSettings] = useState({
    validateStructure: true,
    checkMapping: true,
    autoCorrect: false,
    sendNotification: true,
  });
  // State to control visibility of validation preview and related blocks
  const [fileValidated, setFileValidated] = useState(false);
  // State to store the selected file
  const [selectedFile, setSelectedFile] = useState(null);
  // State to manage drag-and-drop visual feedback
  const [isDragging, setIsDragging] = useState(false);

  // Effect to manage showFileUploadSection based on customerType
  useEffect(() => {
    if (customerType === 'existing') {
      setShowFileUploadSection(true);
      // Optionally reset customer details fields when switching to existing
      setCompanyName('');
      setCompanySize('');
      setCountry('');
    } else {
      setShowFileUploadSection(false);
      setFileValidated(false); // Reset file validation when switching to new customer
    }
  }, [customerType]);

  // Handle change for radio buttons
  const handleCustomerTypeChange = (event) => {
    setCustomerType(event.target.value);
  };

  // Handle change for input fields
  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
  };

  // Handle change for select fields
  const handleSelectChange = (setter) => (event) => {
    setter(event.target.value);
  };

  // Handle change for document type radio buttons
  const handleDocTypeChange = (event) => {
    setDocType(event.target.value);
  };

  // Handle change for validation settings checkboxes
  const handleValidationSettingChange = (event) => {
    setValidationSettings({
      ...validationSettings,
      [event.target.name]: event.target.checked,
    });
  };

  // Helper function to validate file format
  const validateFileFormat = (file) => {
    const allowedExtensions = ['.csv', '.json', '.xml', '.edi'];
    const fileName = file.name.toLowerCase();
    const fileExtension = '.' + fileName.split('.').pop();
    return allowedExtensions.includes(fileExtension);
  };

  // Handle file selection from the input
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (validateFileFormat(file)) {
        setSelectedFile(file);
        console.log('Selected file:', file.name);
      } else {
        setSelectedFile(null);
        alert('Unsupported file format. Please upload a .csv, .json, .xml, or .edi file.');
      }
    } else {
      setSelectedFile(null);
    }
  };

  // Function to trigger the hidden file input click
  const triggerFileInput = () => {
    document.getElementById('fileInput').click();
  };

  // Handle drag over event
  const handleDragOver = (event) => {
    event.preventDefault(); // Prevent default to allow drop
    setIsDragging(true);
  };

  // Handle drag leave event
  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Handle drop event
  const handleDrop = (event) => {
    event.preventDefault(); // Prevent default browser behavior (e.g., opening file)
    setIsDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      if (validateFileFormat(file)) {
        setSelectedFile(file);
        console.log('Dropped file:', file.name);
      } else {
        setSelectedFile(null);
        alert('Unsupported file format. Please upload a .csv, .json, .xml, or .edi file.');
      }
    }
  };

  // Handle form submission for customer details (only for 'new' customer)
  const handleSaveCustomerDetails = () => {
    // Check if required fields are filled for new customer
    if (customerType === 'new' && (!companyName || !country)) {
      alert('Please fill in all required Customer / Company Information fields (Company Name, Country / Region).');
      return;
    }
    console.log('Saving customer details:', { customerType, companyName, companySize, country });
    setShowFileUploadSection(true); // Show file upload section after saving details
    setFileValidated(false); // Reset file validation state when customer details change
  };

  // Handle file upload and validation submission
  const handleUploadAndValidate = (event) => {
    event.preventDefault();
    // In a real application, you would perform file upload and validation here.
    // For this example, we just simulate success and show the validation preview.
    if (fileUploadCustomer && fileFormat && docType && selectedFile) {
        console.log('Uploading and validating:', {
            fileUploadCustomer,
            fileFormat,
            docType,
            validationSettings,
            fileName: selectedFile.name,
        });
        setFileValidated(true); // Set to true to show validation preview and related blocks
    } else {
        alert('Please select Customer Name, File Format, Document Type, and a file for upload.');
    }
  };

  const styles = {
    // Layout
    mainContainer: {
      fontFamily: 'Inter, sans-serif',
      backgroundColor: '#f9fafb',
      color: '#1f2937',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      padding: '24px',
    },
    contentWrapper: { width: '100%', maxWidth: '1200px' },
    header: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
    },
    // The main grid for cards
    mainGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: '24px' },
    // Horizontal grid for the two specific cards
    horizontalGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
        gap: '24px',
        alignItems: 'stretch', // Make cards the same height
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
    },
    // Typography
    h1: { fontSize: '24px', fontWeight: '600', color: '#111827' },
    p: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
    h3: { fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' },
    h4: { fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' },
    label: { display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' },
    // Buttons
    button: {
      padding: '8px 16px',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease-in-out',
      border: 'none',
      cursor: 'pointer',
    },
    primaryButton: { backgroundColor: '#2563eb', color: '#ffffff' },
    secondaryButton: { backgroundColor: '#e5e7eb', color: '#374151' },
    // Form Elements
    input: {
      width: '100%',
      padding: '7px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      outline: 'none',
      fontSize: '14px',
      color: '#111827',
      backgroundColor: '#f9fafb',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      padding: '7px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      outline: 'none',
      fontSize: '14px',
      color: '#111827',
      backgroundColor: '#f9fafb',
    },
    radio: {
      height: '16px',
      width: '16px',
      color: '#2563eb',
      borderColor: '#d1d5db',
      outline: 'none',
      boxShadow: 'none',
    },
    checkbox: {
      height: '16px',
      width: '16px',
      color: '#2563eb',
      borderColor: '#d1d5db',
      borderRadius: '4px',
      outline: 'none',
      boxShadow: 'none',
      marginRight: '12px',
    },
    // File Upload
    fileDropzone: {
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      padding: '32px',
      textAlign: 'center',
      transition: 'border-color 0.3s ease-in-out',
      cursor: 'pointer', // Add cursor pointer to indicate it's clickable
    },
    fileDropzoneDragging: { // New style for dragging state
      borderColor: '#2563eb', // Blue border when dragging
      backgroundColor: '#eff6ff', // Light blue background when dragging
    },
    // New style for selected radio button label
    selectedRadioLabel: {
      borderBottom: '2px solid #2563eb', // Blue line
      paddingBottom: '4px', // Space between text and line
    },
  };

  return (
    <div style={styles.mainContainer}>
      <div style={styles.contentWrapper}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.h1}>Customer Onboarding - Upload EDI/XML Files</h1>
            <p style={styles.p}>Upload EDI 850 (Purchase Order) or 860 (PO Change) files with validation</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button style={{ ...styles.button, ...styles.secondaryButton }}>
              <FileText style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              View History
            </button>
            <button style={{ ...styles.button, ...styles.primaryButton }}>
              <Download style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              Download Templates
            </button>
          </div>
        </div>

        <div style={styles.mainGrid}>
          {/* Customer / Company Information - Conditionally rendered */}
          {customerType === 'new' && (
            <div style={styles.card}>
              <h3 style={styles.h3}>Customer / Company Information</h3>
              <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ ...styles.label, marginBottom: '0' }}>Customer Type:</label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      cursor: 'pointer', // Indicate it's clickable
                      ...(customerType === 'new' ? styles.selectedRadioLabel : {}), // Apply blue line if selected
                    }}
                  >
                    <input
                      type="radio"
                      name="customerType"
                      value="new"
                      checked={customerType === 'new'}
                      onChange={handleCustomerTypeChange}
                      style={styles.radio}
                    />
                    New Customer
                  </label>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      cursor: 'pointer', // Indicate it's clickable
                      ...(customerType === 'existing' ? styles.selectedRadioLabel : {}), // Apply blue line if selected
                    }}
                  >
                    <input
                      type="radio"
                      name="customerType"
                      value="existing"
                      checked={customerType === 'existing'}
                      onChange={handleCustomerTypeChange}
                      style={styles.radio}
                    />
                    Existing Customer
                  </label>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <label htmlFor="companyName" style={styles.label}>
                    Company Name *
                  </label>
                  <input
                    id="companyName"
                    style={styles.input}
                    placeholder="Enter company name"
                    type="text"
                    value={companyName}
                    onChange={handleInputChange(setCompanyName)}
                  />
                </div>
                <div>
                  <label htmlFor="companySize" style={styles.label}>
                    Company Size
                  </label>
                  <select id="companySize" style={styles.select} value={companySize} onChange={handleSelectChange(setCompanySize)}>
                    <option value="">Select company size</option>
                    {companySizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="country" style={styles.label}>
                    Country / Region *
                  </label>
                  <select id="country" style={styles.select} value={country} onChange={handleSelectChange(setCountry)}>
                    <option value="">Select country</option>
                    {countryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ textAlign: 'right', gridColumn: '1 / -1', alignSelf: 'flex-end' }}>
                  <button
                    style={{ ...styles.button, ...styles.primaryButton, padding: '8px 24px' }}
                    type="button"
                    onClick={handleSaveCustomerDetails}
                  >
                    Save Customer Details
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Customer Type Selection (always visible) */}
          {customerType === 'existing' && (
            <div style={styles.card}>
              <h3 style={styles.h3}>Customer Type Selection</h3>
              <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ ...styles.label, marginBottom: '0' }}>Customer Type:</label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      ...(customerType === 'new' ? styles.selectedRadioLabel : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name="customerType"
                      value="new"
                      checked={customerType === 'new'}
                      onChange={handleCustomerTypeChange}
                      style={styles.radio}
                    />
                    New Customer
                  </label>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      ...(customerType === 'existing' ? styles.selectedRadioLabel : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name="customerType"
                      value="existing"
                      checked={customerType === 'existing'}
                      onChange={handleCustomerTypeChange}
                      style={styles.radio}
                    />
                    Existing Customer
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Conditional rendering of the horizontal grid container based on showFileUploadSection */}
          {showFileUploadSection && (
            <div style={styles.horizontalGrid}>
              {/* File Upload Section */}
              <div style={styles.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h3 style={{ ...styles.h3, marginBottom: 0 }}>File Upload & Configuration</h3>
                  <span
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      fontWeight: '500',
                    }}
                  >
                    EDI/XML
                  </span>
                </div>
                <form style={{ display: 'flex', flexDirection: 'column', gap: '24px', flexGrow: 1 }} onSubmit={handleUploadAndValidate}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div>
                      <label htmlFor="fileUploadCustomer" style={styles.label}>Customer Name *</label>
                      <select
                        id="fileUploadCustomer"
                        style={styles.select}
                        value={fileUploadCustomer}
                        onChange={handleSelectChange(setFileUploadCustomer)}
                      >
                        <option value="">Select customer</option>
                        {customerOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="fileFormat" style={styles.label}>File Format *</label>
                      <select
                        id="fileFormat"
                        style={styles.select}
                        value={fileFormat}
                        onChange={handleSelectChange(setFileFormat)}
                      >
                        <option value="">Select format</option>
                        {fileFormatOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ ...styles.label, marginBottom: '12px' }}>Document Type</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                      <div
                        style={{
                          padding: '16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          border: docType === 'EDI 850' ? '2px solid #2563eb' : '1px solid #d1d5db',
                          backgroundColor: docType === 'EDI 850' ? '#eff6ff' : 'transparent',
                        }}
                        onClick={() => setDocType('EDI 850')}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <input type="radio" name="docType" value="EDI 850" checked={docType === 'EDI 850'} onChange={handleDocTypeChange} style={styles.radio}/>
                          <FileText style={{ width: '20px', height: '20px', color: '#2563eb' }} /> {/* Added icon */}
                          <div>
                            <h4 style={{ fontWeight: '500', color: '#111827', margin: 0, fontSize: '14px' }}>EDI 850</h4>
                            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Purchase Order</p>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          padding: '16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          border: docType === 'EDI 860' ? '2px solid #2563eb' : '1px solid #d1d5db',
                          backgroundColor: docType === 'EDI 860' ? '#eff6ff' : 'transparent',
                        }}
                        onClick={() => setDocType('EDI 860')}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <input type="radio" name="docType" value="EDI 860" checked={docType === 'EDI 860'} onChange={handleDocTypeChange} style={styles.radio}/>
                          <FilePen style={{ width: '20px', height: '20px', color: '#2563eb' }} /> {/* Added icon */}
                          <div>
                            <h4 style={{ fontWeight: '500', color: '#111827', margin: 0, fontSize: '14px' }}>EDI 860</h4>
                            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>PO Change</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                    <h4 style={styles.h4}>File Upload</h4>
                    <div
                      style={{ ...styles.fileDropzone, ...(isDragging ? styles.fileDropzoneDragging : {}) }} // Apply dragging style
                      onClick={triggerFileInput}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <CloudUpload style={{ width: '48px', height: '48px', color: '#60a5fa', margin: '0 auto 16px' }} />
                      <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px' }}>Drag & drop or click to upload</p>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>Supported: .csv, .json, .xml, .edi</p>
                      <input
                        type="file"
                        id="fileInput"
                        style={{ display: 'none' }}
                        accept=".csv, .json, .xml, .edi"
                        onChange={handleFileChange}
                      />
                      <button
                        style={{ ...styles.button, ...styles.primaryButton }}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); triggerFileInput(); }}
                      >
                        <FolderOpen style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                        Browse File
                      </button>
                      {selectedFile && (
                        <p style={{ fontSize: '14px', color: '#374151', marginTop: '12px' }}>
                          Selected: <strong>{selectedFile.name}</strong>
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                    <h4 style={styles.h4}>Validation Settings</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                        <input type="checkbox" name="validateStructure" checked={validationSettings.validateStructure} onChange={handleValidationSettingChange} style={styles.checkbox}/>
                        Validate file structure
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                        <input type="checkbox" name="checkMapping" checked={validationSettings.checkMapping} onChange={handleValidationSettingChange} style={styles.checkbox}/>
                        Check customer mapping
                      </label>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '24px', borderTop: '1px solid #e5e7eb', marginTop: 'auto' }}>
                    <button style={{ ...styles.button, ...styles.secondaryButton }} type="button">Draft</button>
                    <button style={{ ...styles.button, ...styles.primaryButton }} type="submit">
                      Upload & Validate
                      <ArrowRight style={{ width: '16px', height: '16px', marginLeft: '8px' }} />
                    </button>
                  </div>
                </form>
              </div>

              {/* Conditional rendering of File Validation Preview based on fileValidated */}
              {fileValidated && (
                <div style={styles.card}>
                  <h3 style={styles.h3}>Validation Preview</h3>
                  <div style={{display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-between'}}>
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                        <div style={{ padding: '16px', backgroundColor: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <CheckCircle style={{ width: '20px', height: '20px', color: '#22c55e' }} />
                            <span style={{ fontWeight: '500', color: '#047857' }}>Structure Valid</span>
                          </div>
                          <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>EDI segments properly formatted</p>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <CheckCircle style={{ width: '20px', height: '20px', color: '#22c55e' }} />
                            <span style={{ fontWeight: '500', color: '#047857' }}>Customer Mapping</span>
                          </div>
                          <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>Rules engine ready</p>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <AlertTriangle style={{ width: '20px', height: '20px', color: '#eab308' }} />
                            <span style={{ fontWeight: '500', color: '#b45309' }}>Warning</span>
                          </div>
                          <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>Missing optional fields detected</p>
                        </div>
                      </div>

                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>Processing Steps</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                            <div style={{ width: '24px', height: '24px', backgroundColor: '#22c55e', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
                              <Check style={{ width: '16px', height: '16px', color: '#ffffff' }} />
                            </div>
                            <span style={{ color: '#374151' }}>File format validation</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                            <div style={{ width: '24px', height: '24px', backgroundColor: '#22c55e', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
                              <Check style={{ width: '16px', height: '16px', color: '#ffffff' }} />
                            </div>
                            <span style={{ color: '#374151' }}>Customer mapping check</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                            <div style={{ width: '24px', height: '24px', border: '2px solid #d1d5db', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
                              <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: '600' }}>3</span>
                            </div>
                            <span style={{ color: '#6b7280' }}>Content validation</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                            <div style={{ width: '24px', height: '24px', border: '2px solid #d1d5db', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
                              <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: '600' }}>4</span>
                            </div>
                            <span style={{ color: '#6b7280' }}>Processing & storage</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '24px' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>Quick Actions</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <button style={{ ...styles.button, ...styles.secondaryButton, width: '100%' }}>
                          <Eye style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          Preview File Content
                        </button>
                        <button style={{ ...styles.button, ...styles.secondaryButton, width: '100%' }}>
                          <Download style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          Download Sample
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Recent Upload History */}
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ ...styles.h3, marginBottom: 0 }}>Recent Upload History</h3>
              <button style={{ color: '#2563eb', fontSize: '14px', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer' }}>
                View All <ArrowRight style={{ width: '12px', height: '12px', marginLeft: '4px', display: 'inline-block' }} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {recentUploads.map((upload, index) => {
                const IconComponent = iconComponents[upload.icon]; // Get the actual component
                return (
                  <div
                    key={index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr auto auto',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '12px 16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#dbeafe',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#2563eb',
                      }}
                    >
                      {IconComponent && <IconComponent style={{ width: '20px', height: '20px' }} />}
                    </div>
                    <div>
                      <p style={{ fontWeight: '500', color: '#111827', margin: 0, fontSize: '14px' }}>{upload.customer}</p>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{upload.docType}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: `${upload.statusColor}20`,
                          color: upload.statusColor,
                        }}
                      >
                        {upload.status}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>{upload.date}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
